/*
 * -placeholder text for new functional component -
 *
 * Copyright (C) 2018, 2019, 2020 Sterilis Solutions LLC all rights reserved.
 */
import React, { useContext } from "react";
import PropTypes from "prop-types";
import {
    copyToClipboard,
    notifySuccessToast,
    notifyFailureToast,
    getPortalBaseURL,
} from "src/components/library/helpers";
import * as Sentry from "@sentry/browser";
import { UserContext } from "src/components/library/UserContext";
import { useLocation } from "react-router-dom";
import { Box, Fab, Tooltip } from "@mui/material";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import { Duration } from "luxon";

const ExportBurninResults = (props) => {
    const user = useContext(UserContext);
    const location = useLocation();

    const createParserUrl = () => {
        let baseUrl = getPortalBaseURL(user["env"]);
        // the query string params in the URL have commas in them, and since we're pasting a CSV into Excel, the commas will
        // automatically force new columns. We must convert the commas to |s,
        // which are also supported by the portal parser as a query string array delimiter
        const queryParamsWithBar = location["search"].split(",").join("|");
        return `${baseUrl}${location["pathname"]}${queryParamsWithBar}`;
    };

    /**
     * Converts an ISO time string to seconds.
     *
     * @param {string} time - The ISO time string to convert.
     * @returns {number|string} The time in seconds if the duration is valid, otherwise "No Data".
     */
    const timeAsSeconds = (time) => {
        const duration = Duration.fromISOTime(time);
        if (duration.isValid) {
            return duration.as("seconds");
        }
        return "No Data";
    }

    /**
     * Converts an ISO time string to minutes.
     *
     * @param {string} time - The ISO time string to be converted.
     * @returns {number|string} The duration in minutes if the input is valid, otherwise "No Data".
     */
    const timeAsMinutes = (time) => {
        const duration = Duration.fromISOTime(time);
        if (duration.isValid) {
            return duration.as("minutes");
        }
        return "No Data";
    }

    /**
     * Returns the provided value or "No Data" if the value is undefined.
     *
     * @param {*} value - The value to check.
     * @returns {*} - The original value if defined, otherwise "No Data".
     */
    const getbitValue = (value) => {
        if (value === undefined) {
            return "No Data";
        }
        return value;
    }

    /**
     * Copies burn-in test results to the clipboard in a CSV format.
     *
     * The function collects various burn-in test metrics from the `props` object,
     * processes them, and then copies the resulting CSV string to the clipboard.
     * It also handles success and failure notifications.
     *
     * @function
     * @name copyValuesToClipboard
     *
     * @throws Will throw an error if copying to the clipboard fails.
     */
    const copyValuesToClipboard = () => {
        const clipboardArray = [];
        const { burninScore, deviceSerial, burninTest1, burninTest2, burninTest3, burninTest4, burninTest5, burninTest6, burninTest7 } = props;
        const passOrFailStatus = props.burninScore >= 90 ? "Pass" : "Fail";
        clipboardArray.push(passOrFailStatus);
        clipboardArray.push(burninScore);
        clipboardArray.push(deviceSerial);

        clipboardArray.push(getbitValue(burninTest1.m0_current));
        clipboardArray.push(getbitValue(burninTest1.m30_current));
        clipboardArray.push(getbitValue(burninTest1.growth_factor));
        clipboardArray.push(getbitValue(burninTest1.max_duty_cycle));
        clipboardArray.push(getbitValue(burninTest1.tooth_temperature));

        clipboardArray.push(getbitValue(burninTest2.scj_percent));
        clipboardArray.push(timeAsSeconds(burninTest2.scj_extension_avg_time));
        clipboardArray.push(getbitValue(burninTest2.shroud_open_percent));
        clipboardArray.push(timeAsSeconds(burninTest2.shroud_open_avg_time));
        clipboardArray.push(getbitValue(burninTest2.shroud_close_percent));
        clipboardArray.push(timeAsSeconds(burninTest2.shroud_close_avg_time));

        clipboardArray.push(getbitValue(burninTest3.pv_min));
        clipboardArray.push(getbitValue(burninTest3.heat_up_rate_delta));
        clipboardArray.push(getbitValue(burninTest3.tr_max));
        clipboardArray.push(getbitValue(burninTest3.tr_average_during_hold));
        clipboardArray.push(getbitValue(burninTest3.pv_high_hold_start));
        clipboardArray.push(getbitValue(burninTest3.pv_high_hold_end));
        clipboardArray.push(getbitValue(burninTest3.pv_drift));
        clipboardArray.push(getbitValue(burninTest3.pv_average_during_hold));
        clipboardArray.push(getbitValue(burninTest3.tp_diff));
        clipboardArray.push(getbitValue(burninTest3.score));
        clipboardArray.push(getbitValue(burninTest4.stag_recovery_count));
        clipboardArray.push(getbitValue(burninTest4.prv_drain_temp));

        clipboardArray.push(getbitValue(burninTest5.pv_min));
        clipboardArray.push(getbitValue(burninTest5.heat_up_rate_delta));
        clipboardArray.push(getbitValue(burninTest5.tr_max));
        clipboardArray.push(getbitValue(burninTest5.tr_average_during_hold));
        clipboardArray.push(getbitValue(burninTest5.pv_average_during_hold));
        clipboardArray.push(timeAsSeconds(burninTest5.open_bottomlid_duration));
        clipboardArray.push(timeAsSeconds(burninTest5.open_toplid_duration));
        clipboardArray.push(getbitValue(burninTest5.tp_diff));
        clipboardArray.push(getbitValue(burninTest5.score));

        clipboardArray.push(getbitValue(burninTest6.grinder_current_hi));
        clipboardArray.push(getbitValue(burninTest6.grinder_current_low));
        clipboardArray.push(getbitValue(burninTest6.grinder_current_noise));
        clipboardArray.push(getbitValue(burninTest6.ia_at_end));
        clipboardArray.push(timeAsSeconds(burninTest6.grinder_time_seconds));
        clipboardArray.push(getbitValue(burninTest6.final_duty_cycle));
        clipboardArray.push(getbitValue(burninTest6.number_stalls));

        clipboardArray.push(getbitValue(burninTest7.pv_min));
        clipboardArray.push(timeAsMinutes(burninTest7.pumpdown_duration));
        clipboardArray.push(getbitValue(burninTest7.tr_average_during_ramp));
        clipboardArray.push(timeAsSeconds(burninTest7.flow_duration));
        clipboardArray.push(getbitValue(burninTest7.heatup_time));
        clipboardArray.push(getbitValue(burninTest7.pv_average_during_hold));
        clipboardArray.push(getbitValue(burninTest7.tv_average_during_hold));
        clipboardArray.push(getbitValue(burninTest7.tr_average_during_hold));
        clipboardArray.push(timeAsMinutes(burninTest7.draining_duration));
        clipboardArray.push(getbitValue(burninTest7.duty_cycle_end));
        clipboardArray.push(timeAsMinutes(burninTest7.duration));
        clipboardArray.push(getbitValue(burninTest7.score));

        const url = createParserUrl();
        clipboardArray.push(url);

        // signature field
        clipboardArray.push("");
        // date field
        clipboardArray.push("");

        try {
            copyToClipboard(clipboardArray.join(","));
            notifySuccessToast("Successfully copied Burnin Data to Clipboard!");
        } catch (e) {
            Sentry.captureException(e);
            notifyFailureToast(
                "Failed to copy Burnin Data to Clipboard. Please refresh this page and try again."
            );
        }
    };
    return (
        <Box
            sx={{
                transform: "translateZ(0px)",
                flexGrow: 1,
                position: "fixed",
                bottom: 150,
                right: 30,
                zIndex: 2000,
            }}
        >
            <Tooltip title="Copy Burnin Results to clipboard" placement="left">
                <Fab
                    color="secondary"
                    aria-label="copy"
                    size="small"
                    onClick={() => copyValuesToClipboard()}
                >
                    <CopyAllIcon />
                </Fab>
            </Tooltip>
        </Box>
    );
};

ExportBurninResults.defaultProps = {
    burninTest1: {},
    burninTest2: {},
    burninTest3: {},
    burninTest4: {},
    burninTest5: {},
    burninTest6: {},
    burninTest7: {},
    deviceSerial: "",
    burninScore: 0,
};

ExportBurninResults.propTypes = {
    deviceSerial: PropTypes.string,
    burninTest1: PropTypes.object,
    burninTest2: PropTypes.object,
    burninTest3: PropTypes.object,
    burninTest4: PropTypes.object,
    burninTest5: PropTypes.object,
    burninTest6: PropTypes.object,
    burninTest7: PropTypes.object,
    burninScore: PropTypes.number,
};

export default ExportBurninResults;
